import { cn } from "@win-reality/react-ui";
import { CSSProperties } from "react";

import { BaserunnerFlags } from "../utils/pitchTagging";

type Base = keyof BaserunnerFlags;

type BaseRunnersProps = {
  baserunners: BaserunnerFlags;
  editable?: boolean;
  onChange?: (baserunners: BaserunnerFlags) => void;
  size?: keyof typeof SIZE_CONFIG;
  // The caller names the diamond (`<area>-base-runners-panel`); the bases inside
  // keep fixed ids and are told apart by data-base. See TEST_IDS.md.
  "data-testid"?: string;
};

// Gap between neighboring bases, as a share of a base's width
const GAP_RATIO = 0.15;
// Corner rounding as a share of a base's width, per Figma
const CORNER_RATIO = 0.16;

/**
 * Layout for bases `baseSize` wide: a 45° diamond spaced the same at every
 * size, so the sizes are scaled copies, in a box that just fits the rotated
 * bases. Offsets place each base's unrotated box; it rotates about its center.
 */
const diamond = (baseSize: number) => {
  const halfDiagonal = baseSize / Math.SQRT2;
  // Center-to-center distance between neighbors, along each axis
  const step = (baseSize * (1 + GAP_RATIO)) / Math.SQRT2;
  const inset = halfDiagonal - baseSize / 2;
  return {
    width: 2 * (step + halfDiagonal),
    height: 2 * halfDiagonal + step,
    baseSize,
    cornerRadius: baseSize * CORNER_RATIO,
    topOffset: inset,
    sideOffset: inset,
    bottomOffset: inset + step,
  };
};

const SIZE_CONFIG = {
  // Plate appearance headers in the sidebar
  small: diamond(13),
  // Scoreboard, plate appearance editor
  default: diamond(25),
  // Results From This Play
  medium: diamond(33),
  // Edit Live Game State
  large: diamond(40),
};

type SizeConfig = (typeof SIZE_CONFIG)[keyof typeof SIZE_CONFIG];

/** Where each base sits in the diamond: second on top, third left, first right. */
const BASE_POSITIONS: {
  base: Base;
  position: (config: SizeConfig) => CSSProperties;
}[] = [
  {
    base: "second",
    position: (config) => ({
      top: `${config.topOffset}px`,
      left: "50%",
      transform: "translateX(-50%) rotate(45deg)",
    }),
  },
  {
    base: "third",
    position: (config) => ({
      top: `${config.bottomOffset}px`,
      left: `${config.sideOffset}px`,
      transform: "rotate(45deg)",
    }),
  },
  {
    base: "first",
    position: (config) => ({
      top: `${config.bottomOffset}px`,
      right: `${config.sideOffset}px`,
      transform: "rotate(45deg)",
    }),
  },
];

const INACTIVE_BASE_COLOR = "dark:bg-[#555656] bg-[rgba(0,0,0,0.10)]";
// Blue marks a runner the user can toggle. Per Figma, in both themes: a
// translucent fill inside a 1px solid border.
const EDITABLE_ACTIVE_BASE_COLOR = "border border-[#5270FF] bg-[#5270FF]/75";
// Hovering an empty editable base previews the blue a tap would give it,
// lighter so it doesn't read as occupied
const EDITABLE_INACTIVE_BASE_HOVER =
  "hover:bg-[#5270FF]/35 dark:hover:bg-[#5270FF]/35";
// Read-only displays (scoreboard, sidebar) show runners in plain contrast so
// blue only ever means "editable".
const READONLY_ACTIVE_BASE_COLOR = "bg-black dark:bg-white";

export const BaseRunners = ({
  baserunners,
  editable = false,
  onChange,
  size = "default",
  "data-testid": testId,
}: BaseRunnersProps) => {
  const config = SIZE_CONFIG[size];

  const toggleBase = (base: Base) => {
    if (!editable || !onChange) return;
    onChange({ ...baserunners, [base]: !baserunners[base] });
  };

  return (
    <div
      data-testid={testId}
      className="relative"
      style={{ width: `${config.width}px`, height: `${config.height}px` }}
    >
      {BASE_POSITIONS.map(({ base, position }) => (
        <div
          key={base}
          data-testid={
            editable ? "base-runners-base-toggle" : "base-runners-base-value"
          }
          data-base={base}
          data-occupied={baserunners[base] ? "true" : "false"}
          role={editable ? "button" : undefined}
          tabIndex={editable ? 0 : undefined}
          aria-pressed={editable ? baserunners[base] : undefined}
          aria-label={editable ? `Runner on ${base}` : undefined}
          className={cn(
            "absolute",
            !baserunners[base] && INACTIVE_BASE_COLOR,
            baserunners[base] &&
              (editable
                ? EDITABLE_ACTIVE_BASE_COLOR
                : READONLY_ACTIVE_BASE_COLOR),
            editable && "cursor-pointer transition-all",
            editable &&
              (baserunners[base]
                ? "hover:opacity-80"
                : EDITABLE_INACTIVE_BASE_HOVER),
          )}
          style={{
            width: `${config.baseSize}px`,
            height: `${config.baseSize}px`,
            borderRadius: `${config.cornerRadius}px`,
            ...position(config),
          }}
          onClick={() => toggleBase(base)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggleBase(base);
            }
          }}
        />
      ))}
    </div>
  );
};
